// Phase SA-5 — Section B autopsy tool surface.
//
// Composes the structured builder (default) + paste mode toggle +
// bookmaker chips + outcome buttons + stake/returns inputs +
// processing state + submit. All form state lives here; the hook
// (useSlipAutopsy from SA-1) owns submission, queue persistence,
// and graceful failure.
//
// Brief §4.1 redesign: every legacy element ported, plus structured
// builder default, chip-style bookmaker selector, GH₵ prefix on
// stake, ANALYZE THIS SLIP button disabled until at least one leg.

import { useMemo, useState } from "react";

import { useAuthSession } from "../../auth/useAuthSession.ts";
import {
  type AutopsyState,
  type DeclaredOutcome,
  type UploadInput,
  useSlipAutopsy,
} from "../../slips/useSlipAutopsy.ts";
import { BookmakerChipRow } from "./BookmakerChipRow.tsx";
import {
  emptyLeg,
  hasSubmittableLegs,
  serializeLegs,
  type StructuredLeg,
} from "./legHelpers.ts";
import { OutcomeButtons } from "./OutcomeButtons.tsx";
import { ProcessingState } from "./ProcessingState.tsx";
import {
  SlipUploadDropzone,
  type SlipUploadValue,
} from "./SlipUploadDropzone.tsx";
import { StructuredLegBuilder } from "./StructuredLegBuilder.tsx";

type Mode = "builder" | "paste" | "upload";

export interface AutopsyFormProps {
  /** Optional: parent can read the SA-1 state machine to render
   * the queued / complete / error_input panels OUTSIDE the form
   * area. When omitted, the form keeps its own internal state and
   * shows the processing UI inline. */
  onStateChange?: (state: AutopsyState) => void;
}

export function AutopsyForm({ onStateChange }: AutopsyFormProps): JSX.Element {
  const {
    state,
    inputError,
    upload: runUpload,
    reset,
  } = useSlipAutopsy();

  // Notify parent on state transitions.
  useMemo(() => {
    if (onStateChange) onStateChange(state);
  }, [state, onStateChange]);

  const { user } = useAuthSession();
  const [mode, setMode] = useState<Mode>("builder");
  const [legs, setLegs] = useState<StructuredLeg[]>(() => [emptyLeg()]);
  const [pasteText, setPasteText] = useState("");
  const [upload, setUpload] = useState<SlipUploadValue | null>(null);
  const [outcome, setOutcome] = useState<DeclaredOutcome>("lost");
  const [stake, setStake] = useState("");
  const [returns, setReturns] = useState("");
  const [bookmaker, setBookmaker] = useState<string | null>(null);

  const builderReady = hasSubmittableLegs(legs);
  const pasteReady = pasteText.trim().length >= 10;
  const uploadReady = upload != null;
  const submitReady =
    mode === "builder"
      ? builderReady
      : mode === "paste"
        ? pasteReady
        : uploadReady;
  const submitting = state === "processing";

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!submitReady || submitting) return;

    const payload: UploadInput = {
      slip_text:
        mode === "builder"
          ? serializeLegs(legs)
          : mode === "paste"
            ? pasteText.trim()
            : "", // upload mode — text comes from SA-6 vision later
      slip_legs:
        mode === "builder"
          ? legs.map((l) => ({
              fixture: l.fixture,
              market: l.market,
              selection: l.selection,
              odds: l.odds,
              outcome: l.outcome,
            }))
          : [],
      input_mode: mode,
      declared_outcome: outcome,
      declared_stake: stake.trim() === "" ? undefined : Number(stake),
      declared_returns: returns.trim() === "" ? undefined : Number(returns),
      bookmaker_slug: bookmaker ?? undefined,
      slip_image_path: mode === "upload" ? upload?.path : undefined,
      slip_image_mime: mode === "upload" ? upload?.mime : undefined,
      slip_image_size_bytes:
        mode === "upload" ? upload?.sizeBytes : undefined,
    };
    await runUpload(payload);
  };

  // ── Processing-state takeover ─────────────────────────────
  // While the Edge fn is running we replace the form with the
  // 4-stage animation. The hook flips state to 'complete' /
  // 'queued' / 'error_input' on resolution; the page surface
  // renders those panels outside the form.
  if (state === "processing") {
    return <ProcessingState active />;
  }

  if (state === "complete") {
    return (
      <div
        data-testid="sa-form-complete"
        style={{
          padding: "20px 18px",
          border: "1px solid var(--sa-signal-green-dim)",
          borderLeft: "2px solid var(--sa-signal-green)",
          borderRadius: 4,
          background: "var(--sa-bg-raised)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--sa-signal-green)",
          }}
        >
          Autopsy complete
        </div>
        <div
          style={{
            fontFamily: "var(--sa-font-body)",
            fontSize: 13.5,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.55,
          }}
        >
          Your profile has been updated above. Per-leg verdict cards land
          in SA-7; the full history view ships in SA-8.
        </div>
        <button
          type="button"
          className="sa-add-leg-btn"
          onClick={reset}
          data-testid="sa-form-reset"
          style={{ marginTop: 0 }}
        >
          Run another autopsy
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="sa-autopsy-form"
      data-mode={mode}
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      {/* Mode toggle */}
      <div
        style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
      >
        <div className="sa-mode-toggle" role="tablist" aria-label="Input mode">
          <button
            type="button"
            data-active={mode === "builder" || undefined}
            onClick={() => setMode("builder")}
            data-testid="sa-mode-builder"
            role="tab"
            aria-selected={mode === "builder"}
          >
            Builder
          </button>
          <button
            type="button"
            data-active={mode === "paste" || undefined}
            onClick={() => setMode("paste")}
            data-testid="sa-mode-paste"
            role="tab"
            aria-selected={mode === "paste"}
          >
            Paste text
          </button>
          <button
            type="button"
            data-active={mode === "upload" || undefined}
            onClick={() => setMode("upload")}
            data-testid="sa-mode-upload"
            role="tab"
            aria-selected={mode === "upload"}
          >
            Upload slip
          </button>
        </div>
        <p
          style={{
            margin: 0,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.06em",
          }}
        >
          The more slips you analyze, the more precisely STRIKZA knows where
          your edge is.
        </p>
      </div>

      {/* Builder / Paste / Upload */}
      {mode === "builder" ? (
        <StructuredLegBuilder legs={legs} onChange={setLegs} />
      ) : mode === "paste" ? (
        <div>
          <label className="sa-form-label" htmlFor="sa-paste-textarea">
            Paste your slip text
          </label>
          <textarea
            id="sa-paste-textarea"
            className="sa-form-input"
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={`Brighton vs Bournemouth — Match Winner — Home @ 1.72\nBayern vs RB Leipzig — BTTS — Yes @ 2.00`}
            data-testid="sa-paste-textarea"
          />
        </div>
      ) : (
        <div>
          <label className="sa-form-label">Upload your slip image</label>
          {user ? (
            <SlipUploadDropzone
              userId={user.id}
              value={upload}
              onUploadComplete={setUpload}
              onClear={() => setUpload(null)}
              disabled={submitting}
            />
          ) : (
            <div
              className="sa-upload-dropzone"
              data-state="idle"
              data-testid="sa-upload-signin-hint"
            >
              <div className="sa-upload-headline">
                Sign in to upload a slip image.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Outcome */}
      <div>
        <label className="sa-form-label">Overall outcome</label>
        <OutcomeButtons value={outcome} onChange={setOutcome} />
      </div>

      {/* Stake + Returns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        <div>
          <label className="sa-form-label" htmlFor="sa-stake-input">
            Stake (optional)
          </label>
          <div className="sa-form-prefix">
            <span className="sa-form-prefix-symbol">GH₵</span>
            <input
              id="sa-stake-input"
              type="number"
              step="0.01"
              min="0"
              className="sa-form-input"
              placeholder="200"
              value={stake}
              onChange={(e) => setStake(e.target.value)}
              data-testid="sa-stake-input"
              style={{ flex: "1 1 auto" }}
            />
          </div>
        </div>
        <div>
          <label className="sa-form-label" htmlFor="sa-returns-input">
            Returns (optional)
          </label>
          <div className="sa-form-prefix">
            <span className="sa-form-prefix-symbol">GH₵</span>
            <input
              id="sa-returns-input"
              type="number"
              step="0.01"
              min="0"
              className="sa-form-input"
              placeholder="0"
              value={returns}
              onChange={(e) => setReturns(e.target.value)}
              data-testid="sa-returns-input"
              style={{ flex: "1 1 auto" }}
            />
          </div>
          <ProfitHint
            outcome={outcome}
            stake={stake}
            returns={returns}
          />
        </div>
      </div>

      {/* Bookmaker chips */}
      <div>
        <label className="sa-form-label">Bookmaker</label>
        <BookmakerChipRow value={bookmaker} onChange={setBookmaker} />
      </div>

      {/* Input validation hint */}
      {state === "error_input" && inputError ? (
        <div
          data-testid="sa-form-input-error"
          style={{
            padding: "10px 12px",
            border: "1px solid var(--sa-hairline-accent)",
            borderLeft: "2px solid var(--sa-accent-saffron)",
            borderRadius: 4,
            background: "var(--sa-amber-glow)",
            fontFamily: "var(--sa-font-body)",
            fontSize: 13,
            color: "var(--sa-fg-primary)",
          }}
        >
          {inputError}
        </div>
      ) : null}

      {/* Submit */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="submit"
          className="sa-submit-btn"
          disabled={!submitReady}
          data-testid="sa-form-submit"
        >
          Analyze this slip
        </button>
      </div>
    </form>
  );
}

function ProfitHint({
  outcome,
  stake,
  returns,
}: {
  outcome: DeclaredOutcome;
  stake: string;
  returns: string;
}): JSX.Element | null {
  if (outcome !== "won") return null;
  const stakeN = Number(stake);
  const returnsN = Number(returns);
  if (!Number.isFinite(stakeN) || !Number.isFinite(returnsN)) return null;
  if (stakeN <= 0 || returnsN <= 0) return null;
  const profit = returnsN - stakeN;
  return (
    <div
      data-testid="sa-profit-hint"
      style={{
        marginTop: 4,
        fontFamily: "var(--sa-font-mono)",
        fontSize: 11,
        letterSpacing: "0.06em",
        color: profit >= 0 ? "var(--sa-signal-green)" : "var(--sa-signal-red)",
      }}
    >
      {profit >= 0
        ? `Profit: GH₵${profit.toFixed(2)}`
        : `Loss: GH₵${Math.abs(profit).toFixed(2)}`}
    </div>
  );
}
